/**
 * Force every UI date format to Pacific Time (America/Los_Angeles).
 *
 * Why this exists
 * ---------------
 * OC Tasker is an LA/Newport-Beach business. Every customer, every tech,
 * and every admin operates on Pacific Time. But ~85 components across
 * the codebase render timestamps with `new Date(iso).toLocaleString()`,
 * `.toLocaleDateString()`, and `.toLocaleTimeString()` — and JavaScript
 * defaults those calls to the **browser's** locale + timezone.
 *
 * That means a tech viewing the app from Arizona during DST sees times
 * shifted an hour off, an admin in a UTC-set Linux VM sees UTC, and so
 * on. The intent — "everything is PT" — silently breaks for anyone
 * whose browser disagrees.
 *
 * The pragmatic, single-file fix is to monkey-patch the three `Date`
 * prototype methods to inject `timeZone: 'America/Los_Angeles'` whenever
 * the caller doesn't already specify a timezone. Existing callers that
 * already pass an explicit `timeZone` (e.g. UTC in the audit log, or
 * the few PT-aware components today) still flow through unchanged.
 *
 * Trade-offs vs touching all 85 files
 * -----------------------------------
 *  ✅ Zero risk of missing a callsite
 *  ✅ Single import to revisit / remove later
 *  ✅ Locale also defaults to "en-US" so date *string formatting* is
 *     consistent regardless of browser locale (no German DD.MM.YYYY etc.)
 *  ⚠️ Monkey-patching globals is generally frowned upon. Acceptable
 *     here because this is a pinned-timezone single-tenant app. If we
 *     ever multi-tenant by city, we'd swap this for an explicit
 *     `formatPT(date, opts)` helper and audit each callsite.
 *
 * Loaded ONCE at the very top of `index.js`, before any component is
 * rendered, so every subsequent date format flows through it.
 */

const PT_TIMEZONE = "America/Los_Angeles";
const DEFAULT_LOCALE = "en-US";

const ORIG_TO_LOCALE_STRING = Date.prototype.toLocaleString;
const ORIG_TO_LOCALE_DATE_STRING = Date.prototype.toLocaleDateString;
const ORIG_TO_LOCALE_TIME_STRING = Date.prototype.toLocaleTimeString;

function withPT(options) {
  // Caller passed nothing → inject our defaults
  if (options === undefined || options === null) {
    return { timeZone: PT_TIMEZONE };
  }
  // Caller passed a non-object (rare; some legacy code passes a string locale here) → leave alone
  if (typeof options !== "object") return options;
  // Caller already specified timeZone → respect it (audit logs sometimes want UTC)
  if (options.timeZone) return options;
  return { ...options, timeZone: PT_TIMEZONE };
}

Date.prototype.toLocaleString = function (locales, options) {
  return ORIG_TO_LOCALE_STRING.call(this, locales || DEFAULT_LOCALE, withPT(options));
};
Date.prototype.toLocaleDateString = function (locales, options) {
  return ORIG_TO_LOCALE_DATE_STRING.call(this, locales || DEFAULT_LOCALE, withPT(options));
};
Date.prototype.toLocaleTimeString = function (locales, options) {
  return ORIG_TO_LOCALE_TIME_STRING.call(this, locales || DEFAULT_LOCALE, withPT(options));
};

// Expose a tiny named helper for components that want to render a date
// in PT explicitly without relying on the monkey-patch (e.g. server-side
// rendering or unit tests where the patch hasn't been applied).
export function formatPT(value, options = {}) {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return ORIG_TO_LOCALE_STRING.call(
    d,
    options.locale || DEFAULT_LOCALE,
    { timeZone: PT_TIMEZONE, ...options }
  );
}

export const PACIFIC_TIMEZONE = PT_TIMEZONE;
